import FuseAnimate from '@/@fuse/core/FuseAnimate';
import { uploadFile } from '@/app/services/imageUpload/imageUpload';
import {
	getCompanySchool,
	getSearchableCityList,
	updateSchool,
} from '@/app/services/schools/schools';
import * as Actions from '@/app/store/actions/';
import { getImageUrl, isImageFile } from '@/utils/utils';
import { Avatar, CircularProgress } from '@material-ui/core';
import { isPossiblePhoneNumber } from 'libphonenumber-js/min';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import tz_lookup from 'tz-lookup';
import './EditSchool.css';
import StyledInput from '@shared/components/StyledInput';
import GradientButton from '@shared/components/GradientButton';
import StyledSelect from '@shared/components/StyledSelect';
import PageHeader from '@shared/components/PageHeader/PageHeader';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { states } from '@shared/constants/states';
import StyledSearchSelect from 'shared/components/StyledSearchSelect';
// import { checkoutTimes } from './checkoutTimes';

const EditSchools = () => {
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const navigate = useNavigate();
	const { id } = useParams();
	const {
		control,
		handleSubmit,
		reset,
		setError,
		formState: { errors },
	} = useForm({
		defaultValues: {
			school_name: '',
			total_students: '',
			school_phone: '',
			address: '',
			street: '',
			country: 'US',
			city: '',
			state_id: '',
			zip_code: '',
			status: 1,
			// checkout_time: '',
			admins: [
				{
					first_name: '',
					last_name: '',
					email: '',
					phone: '',
					designation: '',
				},
			],
		},
	});
	const [row, setRow] = useState({});
	const [otp, setOtp] = useState('');
	const [modifiedRow, setModifiedRow] = useState({
		school_name: '',
		total_students: '',
		school_phone: '',
		address: '',
		street: '',
		country: '',
		state_id: '',
		zip_code: '',
		status: '',
		city: '',
	});
	const [selectedFile, setSelectedFile] = useState(null);
	const [preview, setPreview] = useState(null);
	const [isEditing, setIsEditing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [cityTimezone, setCityTimeZone] = useState('');
	const tzlookup = tz_lookup;
	const [cities, setCities] = useState([]);
	const [citiesLoading, setCitiesLoading] = useState(false);
	const [latLong, setLatLong] = useState({ lat: '', long: '' });
	const [searchCityQuery, setSearchCityQuery] = useState('');
	const [defaultCity, setDefaultCity] = useState({});
	const [defaultState, setDefaultState] = useState({});
	const [isDataLoading, setIsDataLoading] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [previousStateId, setPreviousStateId] = useState(null);

	const selectedStateId = useWatch({ control, name: 'state_id' });

	useEffect(() => {
		let isMounted = true;
		setIsDataLoading(true);
		getCompanySchool(id)
			.then((res) => {
				if (!isMounted) return;
				setRow(res.data);
				reset({
					school_name: res.data.name,
					total_students: res.data.total_students,
					school_phone: res.data.phone,
					address: res.data.address,
					street: res.data.street,
					country: res.data.country_code,
					city: res.data.city,
					state_id: res.data.state_id,
					zip_code: res.data.zip_code,
					status: res.data.status,
					// checkout_time: res.data.checkout_time || { label: '7:00 PM', value: '7:00 PM' },
					admins: {
						first_name: res.data.admins[0].first_name,
						last_name: res.data.admins[0].last_name,
						email: res.data.admins[0].email,
						phone: res.data.admins[0].phone,
						designation: res.data.admins[0].designation,
						user_id: res.data.admins[0].id,
					},
				});
				setDefaultState(res.data.state.name);
				setDefaultCity({ name: res.data.city });
				setOtp(res.data.otp);
				setPreview(res?.data?.logo);
				setPreviousStateId(res.data.state.id);
				setIsInitialLoad(false);
			})
			.catch((err) => {
				console.log(err);
			})
			.finally(() => {
				if (!isMounted) return;
				setIsDataLoading(false);
			});
		return () => {
			isMounted = false;
		};
	}, [id, reset]);

	useEffect(() => {
		if (!isInitialLoad && selectedStateId && selectedStateId !== previousStateId) {
			console.log('resetting');
			reset((values) => ({
				...values,
				city: '',
			}));
			setDefaultCity({});
			setCities([]);
			setPreviousStateId(selectedStateId);
		}
	}, [selectedStateId, isInitialLoad, previousStateId]);

	useEffect(() => {
		if (modifiedRow.state_id && states.length > 0) {
			setDefaultState({ id: defaultState.id, name: defaultState.name });
			setDefaultCity({ name: modifiedRow.city });
		}
	}, [citiesLoading, states]);

	useEffect(() => {
		if (!selectedFile) {
			setPreview(null);
			return;
		}

		const objectUrl = URL.createObjectURL(selectedFile);
		setPreview(objectUrl);

		return () => URL.revokeObjectURL(objectUrl);
	}, [selectedFile]);

	const onSelectFile = (e) => {
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedFile(null);
			return;
		}
		const file = e.target.files[0];
		if (!isImageFile(file)) {
			dispatch(Actions.showMessage({ message: 'Please select an image file', variant: 'error' }));
		} else {
			setSelectedFile(e.target.files[0]);
		}
	};

	const handleCancel = () => {
		setModifiedRow({
			school_name: row?.name,
			school_phone: row?.phone,
			address: row?.address,
			street: row?.street || '',
			total_students: row?.total_students,
			country: row?.country_code,
			state_id: row?.state_id,
			city: row?.city,
			zip_code: row?.zip_code,
			status: row?.status,
			// checkout_time: row?.checkout_time || { label: '7:00 PM', value: '7:00 PM' },
		});
		setIsEditing(false);
	};

	const onSubmit = (data) => {
		console.log(data);
		setIsSaving(true);
		const payload = {
			address: data.address,
			city: data.city,
			country_code: data.country,
			school_admins: [
				{
					...data.admins,
				},
			],
			school_name: data.school_name,
			school_phone: data.school_phone,
			state_id: data.state_id,
			status: data.status,
			street: data.street,
			timezone: cityTimezone,
			timezone_checkin: cityTimezone,
			total_students: data.total_students,
			zip_code: data.zip_code,
			// checkout_time: data.checkout_time,
		};

		if (selectedFile) {
			const filename = getImageUrl(selectedFile);
			uploadFile(selectedFile, filename)
				.then((response) => {
					payload.school_profile_image = `${import.meta.env.VITE_S3_BASE_URL}${response}`;
					updateSchool(payload, row.id)
						.then((res) => {
							dispatch(
								Actions.showMessage({
									message: res.data.message,
									variant: 'success',
								})
							);
							navigate('/schools');
						})
						.catch((err) => {
							setIsSaving(false);
							dispatch(
								Actions.showMessage({
									message: 'Failed to update School Data',
								})
							);
							if (err?.response?.data?.errors) {
								Object.entries(err.response.data.errors).forEach(([key, value]) => {
									setError(key, { type: 'manual', message: value });
								});
							}
						})
						.finally(() => {
							setIsSaving(false);
						});
				})
				.catch(() => {
					setIsSaving(false);
					dispatch(
						Actions.showMessage({
							message: 'Failed to update School Data',
						})
					);
				});
		} else {
			updateSchool(payload, row.id)
				.then((res) => {
					dispatch(
						Actions.showMessage({
							message: res.data.message,
							variant: 'success',
						})
					);
					navigate('/schools');
				})
				.catch((err) => {
					setIsSaving(false);
					dispatch(
						Actions.showMessage({
							message: 'Failed to update School Data',
						})
					);
					if (err?.response?.data?.errors) {
						Object.entries(err.response.data.errors).forEach(([key, value]) => {
							setError(key, { type: 'manual', message: value });
						});
					}
				})
				.finally(() => {
					setIsSaving(false);
				});
		}
	};

	useEffect(() => {
		let isMounted = true;
		setCitiesLoading(true);
		const timeout = setTimeout(() => {
			if (!isMounted) return;
			setCities([]);
			if (!searchCityQuery && !selectedStateId) {
				setCitiesLoading(false);
				setCities([]);
			} else {
				getSearchableCityList(selectedStateId, searchCityQuery, 1)
					.then((res) => {
						if (!isMounted) return;
						setCities(res.data.data);
					})
					.catch(() => {
						if (!isMounted) return;
						dispatch(
							Actions.showMessage({
								message: 'Failed to get cities.',
								autoHideDuration: 1500,
								variant: 'error',
							})
						);
					})
					.finally(() => {
						if (!isMounted) return;
						setCitiesLoading(false);
					});
			}
		}, 1000);
		return () => {
			isMounted = false;
			clearTimeout(timeout);
		};
	}, [modifiedRow.state_id, searchCityQuery]);

	useEffect(() => {
		if (latLong.lat !== '' && latLong.long !== '') {
			const timezone = tzlookup(latLong.lat, latLong.long);
			setCityTimeZone(timezone);
			setModifiedRow((prev) => ({
				...prev,
				timezone,
				timezone_checkin: timezone,
			}));
		}
	}, [latLong]);


	const handleBack = () => {
		navigate('/schools');
	};

	return (
		<FuseAnimate animation="transition.slideLeftIn" duration={600}>
			<div className="m-auto p-4 !mb-[162px]">
				<div className="items-end mb-8 sm:mb-20 justify-between overall-box">
					<div className="flex items-center gap-4 md:gap-0 justify-between">
						<PageHeader title="School Details" onBack={handleBack} />
						<div className="personal-button">
							{!isEditing ? (
								<GradientButton
									onClick={() => {
										setIsEditing(true);
									}}
									variant="blurple"
									size="small"
								>
									Edit
								</GradientButton>
							) : (
								<div />
							)}
						</div>
					</div>

					<div className="edit-input mt-8">
						<div className="flex flex-col md:flex-row gap-32 md:gap-48 mt-8">
							{!isEditing ? (
								<Avatar src={preview} style={{ width: 120, height: 120 }} />
							) : (
								<div
									className="cursor-pointer"
									onClick={() => inputRef.current.click()}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											inputRef.current.click();
										}
									}}
									role="button"
									tabIndex={0}
								>
									<Avatar src={preview} style={{ width: 120, height: 120 }} />
									<div className="school-pp-overlay">
										<i className="fa fa-2x fa-camera" />
									</div>
									<input
										accept="image/*"
										className="hidden"
										id="image"
										name="file"
										onChange={onSelectFile}
										ref={inputRef}
										type="file"
									/>
								</div>
							)}
							{isDataLoading ? (
								<div className="flex justify-center p-8">
									<CircularProgress />
								</div>
							) : (
								<form onSubmit={handleSubmit(onSubmit)} className="w-full">
									<div className="flex justify-between items-center">
										<h2 className="text-pd-xl font-bold mb-32">School Admin Info</h2>
									</div>

									<fieldset
										className={`my-24 sm:my-40
											grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-24 gap-y-48`}
									>
										<Controller
											control={control}
											name="admins.first_name"
											rules={{
												required: 'First name is required',
												pattern: {
													value: /^[a-zA-Z ]+$/,
													message: 'Please enter a valid value',
												},
											}}
											render={({ field }) => (
												<StyledInput
													{...field}
													disabled={!isEditing}
													error={!!errors?.admins?.first_name}
													helperText={errors?.admins?.first_name?.message}
													label="First Name"
													variant={!isEditing ? 'standard' : 'outlined'}
													isInformational={!isEditing}
													fullWidth
													required={isEditing}
												/>
											)}
										/>
										<Controller
											control={control}
											name="admins.last_name"
											rules={{
												required: 'Last name is required',
												pattern: {
													value: /^[a-zA-Z ]+$/,
													message: 'Please enter a valid value',
												},
											}}
											render={({ field }) => (
												<StyledInput
													{...field}
													disabled={!isEditing}
													error={!!errors?.admins?.last_name}
													helperText={errors?.admins?.last_name?.message}
													label="Last Name"
													variant={!isEditing ? 'standard' : 'outlined'}
													isInformational={!isEditing}
													fullWidth
													required={isEditing}
												/>
											)}
										/>
										<Controller
											control={control}
											name="admins.email"
											rules={{
												required: 'Email is required',
												pattern: {
													value: /^\S+@\S+\.\S+$/,
													message: 'Please enter a valid email',
												},
											}}
											render={({ field }) => (
												<StyledInput
													{...field}
													disabled={!isEditing}
													error={!!errors?.admins?.email}
													helperText={errors?.admins?.email?.message}
													label="Email Address"
													variant={!isEditing ? 'standard' : 'outlined'}
													isInformational={!isEditing}
													fullWidth
													required={isEditing}
												/>
											)}
										/>
										<Controller
											control={control}
											name="admins.phone"
											rules={{
												required: 'Phone number is required',
												validate: (value) => isPossiblePhoneNumber(value, 'US') || 'Please enter a valid phone number',
											}}
											render={({ field }) => (
												<StyledInput
													{...field}
													disabled={!isEditing}
													error={!!errors?.admins?.phone}
													helperText={errors?.admins?.phone?.message}
													label="Contact Number"
													variant={!isEditing ? 'standard' : 'outlined'}
													isInformational={!isEditing}
													fullWidth
													type="phone"
													required={isEditing}
												/>
											)}
										/>
										<Controller
											control={control}
											name="admins.designation"
											rules={{
												required: 'Designation is required',
											}}
											render={({ field }) => (
												<StyledInput
													{...field}
													disabled={!isEditing}
													error={!!errors?.admins?.designation}
													helperText={errors?.admins?.designation?.message}
													label="Designation"
													variant={!isEditing ? 'standard' : 'outlined'}
													isInformational={!isEditing}
													fullWidth
													required={isEditing}
												/>
											)}
										/>
									</fieldset>

									<div className="flex justify-between items-center">
										<h2 className="text-pd-xl font-bold">School Info</h2>
									</div>
									<fieldset className="mt-48 md:mt-64">
										<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-24 gap-y-48">
											<StyledInput
												disabled
												id="school_code"
												label="School Code"
												name="school_code"
												value={otp}
												variant={!isEditing ? 'standard' : 'outlined'}
												isInformational={!isEditing}
											/>
											<Controller
												control={control}
												name="school_name"
												rules={{
													required: 'School name is required',
													pattern: {
														value: /^[a-zA-Z ]+$/,
														message: 'Please enter a valid value',
													},
												}}
												render={({ field }) => (
													<StyledInput
														{...field}
														disabled={!isEditing}
														error={!!errors.school_name}
														helperText={errors?.school_name?.message}
														label="Name of School"
														required={isEditing}
														variant={!isEditing ? 'standard' : 'outlined'}
														isInformational={!isEditing}
													/>
												)}
											/>
											<Controller
												control={control}
												name="total_students"
												render={({ field }) => (
													<StyledInput
														{...field}
														disabled
														label="Total Students"
														variant={!isEditing ? 'standard' : 'outlined'}
														isInformational={!isEditing}
														fullWidth
													/>
												)}
											/>
											<Controller
												control={control}
												name="school_phone"
												rules={{
													required: 'Phone number is required',
													validate: (value) => isPossiblePhoneNumber(value, 'US') || 'Please enter valid phone number',
												}}
												render={({ field }) => (
													<StyledInput
														{...field}
														disabled={!isEditing}
														error={!!errors.school_phone}
														helperText={errors?.school_phone?.message}
														label="Contact Number"
														required={isEditing}
														variant={!isEditing ? 'standard' : 'outlined'}
														isInformational={!isEditing}
													/>
												)}
											/>
											<Controller
												control={control}
												name="address"
												rules={{
													required: 'Address is required',
												}}
												render={({ field }) => (
													<StyledInput
														{...field}
														disabled={!isEditing}
														error={!!errors.address}
														helperText={errors?.address?.message}
														label="Address 1"
														required={isEditing}
														variant={!isEditing ? 'standard' : 'outlined'}
														isInformational={!isEditing}
													/>
												)}
											/>
											<Controller
												control={control}
												name="street"
												render={({ field }) => (
													<StyledInput
														{...field}
														disabled={!isEditing}
														error={!!errors.street}
														helperText={errors?.street?.message}
														label="Address 2"
														variant={!isEditing ? 'standard' : 'outlined'}
														isInformational={!isEditing}
														fullWidth
													/>
												)}
											/>
											<StyledInput
												disabled
												value="United States"
												label="Country"
												variant={!isEditing ? 'standard' : 'outlined'}
												isInformational={!isEditing}
												fullWidth
											/>
											<Controller
												control={control}
												name="state_id"
												rules={{
													required: 'State is required',
												}}
												render={({ field }) => (
													<StyledSelect
														{...field}
														disabled={!isEditing}
														error={!!errors.state_id}
														helperText={errors?.state_id?.message}
														label="State"
														variant={!isEditing ? 'standard' : 'outlined'}
														isInformational={!isEditing}
														labelClasses="text-black/38"
														options={states}
														required={isEditing}
														fullWidth
													/>
												)}
											/>
											<Controller
												control={control}
												name="city"
												rules={{
													required: 'City is required',
												}}
												render={({ field }) => (
													<StyledSearchSelect
														disabled={!isEditing}
														error={!!errors.city}
														helperText={errors?.city?.message}
														label="City"
														options={cities.map((c) => ({
															label: c.name,
															value: c.name,
															meta: c.meta,
														}))}
														placeholder="Select City"
														required={isEditing}
														variant={!isEditing ? 'standard' : 'outlined'}
														labelClasses="text-black/38"
														isInformational={!isEditing}
														resultsLoading={citiesLoading}
														widthClass="w-auto justify-end"
														setControlledQuery={setSearchCityQuery}
														value={
															defaultCity
																? {
																		label: defaultCity.name,
																		value: defaultCity.name,
																		meta: defaultCity.meta,
																	}
																: null
														}
														onChange={(selected) => {
															console.log(selected);
															field.onChange(selected?.value);
															setDefaultCity({
																name: selected?.value,
																meta: selected?.meta,
															});
															if (selected?.meta) {
																const value = JSON.parse(selected.meta);
																setLatLong({
																	lat: value.latitude,
																	long: value.longitude,
																});
															}
														}}
													/>
												)}
											/>
											<Controller
												control={control}
												name="zip_code"
												rules={{
													required: 'Zip code is required',
													pattern: {
														value: /^\d{5}$/,
														message: 'Please enter a valid 5-digit zip code',
													},
												}}
												render={({ field }) => (
													<StyledInput
														{...field}
														disabled={!isEditing}
														error={!!errors.zip_code}
														helperText={errors?.zip_code?.message}
														label="Zipcode"
														required={isEditing}
														variant={!isEditing ? 'standard' : 'outlined'}
														isInformational={!isEditing}
													/>
												)}
											/>
											<Controller
												control={control}
												name="status"
												render={({ field }) => (
													<StyledSelect
														{...field}
														disabled={!isEditing}
														label="Status"
														variant={!isEditing ? 'standard' : 'outlined'}
														labelClasses="text-black/38"
														isInformational={!isEditing}
														fullWidth
														options={[
															{ label: 'Active', value: 1 },
															{ label: 'Inactive', value: 0 },
														]}
													/>
												)}
											/>
											{/* <Controller
												control={control}
												name="checkout_time"
												render={({ field }) => (
													<StyledSelect
														{...field}
														disabled={!isEditing}
														label="Auto Check-out Time"
														variant={!isEditing ? 'standard' : 'outlined'}
														isInformational={!isEditing}
														fullWidth
														labelClasses="text-black/38"
														options={checkoutTimes}
													/>
												)}
											/> */}
										</div>
									</fieldset>
									{isEditing ? (
										<div className="flex justify-center md:justify-end mt-48 md:mt-64">
											{!isSaving ? (
												<div className="flex gap-16">
													<GradientButton onClick={handleCancel} variant="secondary" size="small">
														Cancel
													</GradientButton>
													<GradientButton type="submit" variant="blurple" size="small">
														Update
													</GradientButton>
												</div>
											) : (
												<CircularProgress />
											)}
										</div>
									) : null}
								</form>
							)}
						</div>
					</div>
				</div>
			</div>
		</FuseAnimate>
	);
};

export default EditSchools;
